// redux/slices/aftercareSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { resetStore } from "./resetStore";
import { clearJwtToken } from "../../Utils/authStorage";
import { apiRequest } from "../../Utils/apiClient";

/* ========================= HELPERS ========================= */

const getError = (error, fallback) =>
    error?.response?.data?.error ||
    error?.response?.data ||
    error?.message ||
    fallback;

/* ========================= THUNKS ========================= */

export const fetchAppointmentAftercare = createAsyncThunk(
    "aftercare/fetchAppointment",
    async ({ appointmentId }, { rejectWithValue }) => {
        try {
            if (!appointmentId) throw new Error("ID встречи не передан");

            const response = await apiRequest(
                "GET",
                `/new-appointment/appointment/${encodeURIComponent(appointmentId)}/aftercare`
            );

            const data = response?.data ?? response;

            if (!Array.isArray(data)) {
                throw new Error("Invalid aftercare response");
            }

            return data;
        } catch (error) {
            const status = error?.response?.status;
            if (status === 401 || status === 403) {
                try {
                    await clearJwtToken();
                } catch { }
            }

            return rejectWithValue(getError(error, "Failed to fetch aftercare status"));
        }
    }
);

/* ========================= STATE ========================= */

const initialState = {
    byAppointmentId: {},
    statusByAppointmentId: {},
    errorByAppointmentId: {},
    requestIdByAppointmentId: {},
};

/* ========================= SLICE ========================= */

const aftercareSlice = createSlice({
    name: "aftercare",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchAppointmentAftercare.pending, (state, action) => {
                const id = String(action.meta.arg.appointmentId);

                state.statusByAppointmentId[id] = "loading";
                state.errorByAppointmentId[id] = null;
                state.requestIdByAppointmentId[id] = action.meta.requestId;
            })
            .addCase(fetchAppointmentAftercare.fulfilled, (state, action) => {
                const id = String(action.meta.arg.appointmentId);

                // Ответ устаревшего запроса не перетирает более свежий.
                if (state.requestIdByAppointmentId[id] !== action.meta.requestId) return;

                state.byAppointmentId[id] = action.payload;
                state.statusByAppointmentId[id] = "succeeded";
                delete state.requestIdByAppointmentId[id];
            })
            .addCase(fetchAppointmentAftercare.rejected, (state, action) => {
                const id = String(action.meta.arg.appointmentId);

                if (state.requestIdByAppointmentId[id] !== action.meta.requestId) return;

                state.statusByAppointmentId[id] = "failed";
                state.errorByAppointmentId[id] = action.payload || action.error?.message;
                delete state.requestIdByAppointmentId[id];
            })
            .addCase(resetStore, () => initialState);
    },
});

export default aftercareSlice.reducer;